class CartDrawerDiscount extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.messageEl = this.querySelector('.cart-drawer__discount-message');
    this.submitButton = this.querySelector('[type="submit"]');
    this.input = this.querySelector('input[name="discount"]');

    this.form?.addEventListener('submit', this.onSubmit.bind(this));
  }

  get discountRoot() {
    return this.dataset.discountRoot || '/discount/';
  }

  setLoading(isLoading) {
    if (this.submitButton) {
      this.submitButton.disabled = isLoading;
      this.submitButton.setAttribute('aria-busy', isLoading);
    }
    if (this.input) this.input.disabled = isLoading;
  }

  hideMessage() {
    if (!this.messageEl) return;
    this.messageEl.hidden = true;
    this.messageEl.textContent = '';
    this.messageEl.classList.remove('cart-drawer__discount-message--error', 'cart-drawer__discount-message--success');
  }

  showMessage(text, type) {
    if (!this.messageEl) return;
    this.messageEl.hidden = false;
    this.messageEl.textContent = text;
    this.messageEl.classList.toggle('cart-drawer__discount-message--error', type === 'error');
    this.messageEl.classList.toggle('cart-drawer__discount-message--success', type === 'success');
  }

  isDiscountApplied(cartAfter, code, cartBefore) {
    const normalized = code.toUpperCase();
    const codes = cartAfter.discount_codes || [];
    const match = codes.find((entry) => entry.code?.toUpperCase() === normalized);

    if (match) return match.applicable !== false;

    const discountAfter = cartAfter.total_discount || 0;
    const discountBefore = cartBefore.total_discount || 0;
    if (discountAfter > discountBefore) return true;

    const appsAfter = cartAfter.cart_level_discount_applications || [];
    const appsBefore = cartBefore.cart_level_discount_applications || [];
    return appsAfter.length > appsBefore.length;
  }

  async fetchCart() {
    const response = await fetch(`${routes.cart_url}.js`);
    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  }

  buildDiscountUrl(code) {
    const redirect = window.location.pathname + window.location.search;
    const params = new URLSearchParams({ redirect });
    return `${this.discountRoot}${encodeURIComponent(code)}?${params}`;
  }

  /**
   * Shopify applies discount codes via GET /discount/{code}, not POST.
   * A hidden iframe loads that URL so the session cookie is set without leaving the page.
   */
  applyDiscountCode(code) {
    const discountUrl = this.buildDiscountUrl(code);

    return new Promise((resolve, reject) => {
      const iframe = document.createElement('iframe');
      iframe.setAttribute('aria-hidden', 'true');
      iframe.setAttribute('tabindex', '-1');
      iframe.style.cssText =
        'position:absolute;width:0;height:0;border:0;clip:rect(0,0,0,0);overflow:hidden;pointer-events:none';

      let settled = false;

      const settle = (error) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        iframe.remove();
        if (error) reject(error);
        else resolve();
      };

      const timeoutId = window.setTimeout(() => settle(), 5000);

      iframe.addEventListener('load', () => {
        window.setTimeout(() => settle(), 200);
      });

      iframe.addEventListener('error', () => {
        settle(new Error('Discount request failed'));
      });

      iframe.src = discountUrl;
      document.body.appendChild(iframe);
    });
  }

  async refreshCartDrawer() {
    const cartDrawerItems = document.querySelector('cart-drawer-items');
    if (cartDrawerItems?.onCartUpdate) {
      await cartDrawerItems.onCartUpdate();
    }
  }

  static syncSuccessMessage(cart) {
    const discountEl = document.querySelector('cart-drawer-discount');
    if (!discountEl) return;

    const strings = window.cartDrawerDiscountStrings || {};
    const hasPromo = (cart?.cart_level_discount_applications || []).length > 0;
    const messageEl = discountEl.querySelector('.cart-drawer__discount-message');

    if (hasPromo) {
      discountEl.showMessage(strings.success || 'Discount applied.', 'success');
    } else if (messageEl?.classList.contains('cart-drawer__discount-message--success')) {
      discountEl.hideMessage();
    }
  }

  async onSubmit(event) {
    event.preventDefault();

    const code = this.input?.value?.trim();
    const strings = window.cartDrawerDiscountStrings || {};

    if (!code) {
      this.showMessage(strings.empty || 'Please enter a discount code.', 'error');
      return;
    }

    this.setLoading(true);
    this.hideMessage();

    try {
      const cartBefore = await this.fetchCart();
      await this.applyDiscountCode(code);
      const cartAfter = await this.fetchCart();

      if (!this.isDiscountApplied(cartAfter, code, cartBefore)) {
        this.showMessage(strings.invalid || 'Enter a valid discount code.', 'error');
        return;
      }

      await this.refreshCartDrawer();
      this.showMessage(strings.success || 'Discount applied.', 'success');
      CartDrawerDiscount.syncCheckoutLinks(cartAfter);

      if (typeof publish === 'function') {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer-discount', cartData: cartAfter });
      }
    } catch (error) {
      console.error(error);
      this.showMessage(strings.error || 'Could not apply the discount code. Please try again.', 'error');
    } finally {
      this.setLoading(false);
    }
  }

  static getAppliedDiscountCode(cart) {
    const codes = cart?.discount_codes || [];
    const applicable = codes.find((entry) => entry.applicable !== false);
    return applicable?.code || null;
  }

  static syncCheckoutLinks(cart) {
    const code = CartDrawerDiscount.getAppliedDiscountCode(cart);
    const checkoutButton = document.getElementById('CartDrawer-Checkout');
    if (!checkoutButton) return;

    const checkoutUrl = CartDrawerDiscount.buildCheckoutUrl(code, checkoutButton.getAttribute('href'));
    checkoutButton.setAttribute('href', checkoutUrl);
  }

  static buildCheckoutUrl(discountCode, baseHref) {
    const checkoutUrl = new URL(baseHref || '/checkout', window.location.origin);
    if (!checkoutUrl.searchParams.has('skip_shop_pay')) {
      checkoutUrl.searchParams.set('skip_shop_pay', 'true');
    }
    if (discountCode) {
      checkoutUrl.searchParams.set('discount', discountCode);
    } else {
      checkoutUrl.searchParams.delete('discount');
    }
    return checkoutUrl.toString();
  }
}

customElements.define('cart-drawer-discount', CartDrawerDiscount);
